import { useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'react-tippy'
import { useNavigate } from 'react-router-dom'

import { UserCirclePlusIcon, UsersThreeIcon } from '@phosphor-icons/react'

import { Button, SearchInput, ToastContext, UsersGroup } from '@reveni-io/reveni-ui'

import { userPermissions } from '@/common/constants'
import AppContext from '@/common/context/app.context'
import useUserPermissions from '@/common/hooks/useUserPermissions/useUserPermissions'
import DeleteGroupConfirmationModal from '@/configuration/components/DeleteGroupConfirmationModal/DeleteGroupConfirmationModal'
import DeleteUserConfirmationModal from '@/configuration/components/DeleteUserConfirmationModal/DeleteUserConfirmationModal'
import ErrorDeleteGroupModal from '@/configuration/components/ErrorDeleteGroupModal/ErrorDeleteGroupModal'
import routes from '@/Router/routes.constants'

import useUsersConfiguration from './hooks/useUsersConfiguration'
import UsersConfigurationSkeleton from './UsersConfigurationSkeleton/UsersConfigurationSkeleton'
import { getAvailableGroups, hasElegibleGroups } from './helpers'
import * as Styles from './styles'

const UsersConfiguration = () => {
  const { info } = useContext(AppContext)
  const { setToastInfo } = useContext(ToastContext)
  const navigate = useNavigate()
  const { t } = useTranslation()

  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [showDeleteUserConfirmationModal, setShowDeleteUserConfirmationModal] = useState(false)
  const [showDeleteGroupConfirmationModal, setShowDeleteGroupConfirmationModal] = useState(false)
  const [search, setSearch] = useState('')
  const [searchResults, setSearchResults] = useState([])

  const {
    userGroups,
    userGroupsLoading,
    refetchUserGroups,
    deleteUser,
    deletingUser,
    resetUserPassword,
    migrateUserGroup,
    migratingUserGroup,
    deleteUserGroup,
    deletingUserGroup,
    getUsers,
  } = useUsersConfiguration(info?.selectedStore?.id)

  const { hasPermission, isOwner } = useUserPermissions()

  const canEditUsers = isOwner() || hasPermission(userPermissions.editUsers)
  const canEditGroups = isOwner() || hasPermission(userPermissions.editGroups)

  const handleDeleteGroup = async group => {
    if (group?.users.length > 0) {
      setSelectedGroup(group)
      setShowDeleteGroupConfirmationModal(true)
    } else {
      try {
        await deleteUserGroup({ storeId: info.selectedStore.id, groupId: group?.id })
        setToastInfo({ show: true, message: t('Grupo eliminado'), type: 'success' })
        setShowDeleteGroupConfirmationModal(false)
        refetchUserGroups()
      } catch (error) {
        setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
      }
    }
  }

  const onEditGroup = group =>
    navigate(`${routes.configurationPage.group}/${group?.id}`, { state: { childPageTitle: group?.name } })

  const onEditUser = user =>
    navigate(`${routes.configurationPage.users}/${user.id}`, { state: { childPageTitle: user?.full_name } })

  const handleResetPassword = async user => {
    try {
      await resetUserPassword({ storeId: info.selectedStore.id, userId: user.id })
      setToastInfo({ show: true, message: t('Contraseña restablecida'), type: 'success' })
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const handleDeleteUser = user => {
    setSelectedUser(user)
    setShowDeleteUserConfirmationModal(true)
  }

  const onConfirmDeleteUser = async () => {
    try {
      await deleteUser({ storeId: info.selectedStore.id, userId: selectedUser?.id })
      setToastInfo({ show: true, message: t('Usuario eliminado'), type: 'success' })
      setShowDeleteUserConfirmationModal(false)
      refetchUserGroups()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onConfirmDeleteGroup = async newGroupId => {
    try {
      await migrateUserGroup({ storeId: info.selectedStore.id, groupId: selectedGroup?.id, newGroupId })
      await deleteUserGroup({ storeId: info.selectedStore.id, groupId: selectedGroup?.id })
      setToastInfo({ show: true, message: t('Grupo eliminado'), type: 'success' })
      setShowDeleteGroupConfirmationModal(false)
      refetchUserGroups()
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  const onAddGroup = () => navigate(`${routes.configurationPage.group}/new`)

  const onAddUser = group => {
    navigate(`${routes.configurationPage.users}/new`, { state: { selectedGroupId: group?.id } })
  }

  const handleSearch = async value => {
    setSearch(value)
    try {
      const data = await getUsers({ storeId: info.selectedStore.id, params: { search: value } })

      const usersResultsGroup = {
        color: '#000000',
        default: true,
        name: t('Resultados'),
        users: data?.results,
      }
      setSearchResults([usersResultsGroup])
    } catch (error) {
      setToastInfo({ show: true, message: error.errorMessage, type: 'error' })
    }
  }

  if (userGroupsLoading || deletingUser) {
    return <UsersConfigurationSkeleton />
  }

  return (
    <>
      <Styles.Actions>
        <SearchInput placeholder={t('Nombre, email')} onSearch={value => handleSearch(value)} />
        {canEditGroups && (
          <Button variant="text" onClick={onAddGroup}>
            <UsersThreeIcon size={24} />
            {t('Añadir grupo')}
          </Button>
        )}
        {canEditUsers && (
          <Tooltip title={t('Añade un grupo antes de añadir usuarios')} disabled={hasElegibleGroups(userGroups)}>
            <Button variant="text" onClick={() => onAddUser()} disabled={!hasElegibleGroups(userGroups)}>
              <UserCirclePlusIcon size={24} />
              {t('Añadir usuario')}
            </Button>
          </Tooltip>
        )}
      </Styles.Actions>
      <Styles.GroupsContainer>
        {search &&
          searchResults?.map(results => (
            <UsersGroup
              key={results?.id}
              group={results}
              onEditUser={onEditUser}
              onDeleteUser={handleDeleteUser}
              onResetPassword={handleResetPassword}
              showActions={canEditGroups}
              showUserActions={canEditUsers}
            />
          ))}
        {userGroups?.map(group => (
          <>
            <UsersGroup
              key={group?.id}
              group={group}
              onEditGroup={() => onEditGroup(group)}
              onDeleteGroup={() => handleDeleteGroup(group)}
              onEditUser={onEditUser}
              onDeleteUser={handleDeleteUser}
              onResetPassword={handleResetPassword}
              onAddUser={() => onAddUser(group)}
              showActions={canEditGroups}
              showUserActions={canEditUsers}
            />
          </>
        ))}
      </Styles.GroupsContainer>
      <DeleteUserConfirmationModal
        show={showDeleteUserConfirmationModal}
        onClose={() => setShowDeleteUserConfirmationModal(false)}
        onConfirm={onConfirmDeleteUser}
        userName={selectedUser?.first_name}
        storeName={info?.selectedStore?.name}
        confirming={deletingUser}
      />
      <DeleteGroupConfirmationModal
        show={showDeleteGroupConfirmationModal && getAvailableGroups(userGroups, selectedGroup?.id).length > 0}
        onClose={() => setShowDeleteGroupConfirmationModal(false)}
        onConfirm={onConfirmDeleteGroup}
        groupName={selectedGroup?.name}
        groups={getAvailableGroups(userGroups, selectedGroup?.id)}
        confirming={migratingUserGroup || deletingUserGroup}
      />
      <ErrorDeleteGroupModal
        show={showDeleteGroupConfirmationModal && getAvailableGroups(userGroups, selectedGroup?.id).length === 0}
        onClose={() => setShowDeleteGroupConfirmationModal(false)}
      />
    </>
  )
}

export default UsersConfiguration
